import { getAllMarkListApi } from '@/api/hall/bid-open-hall'
import { generateTableHtml } from '@/utils/generate-html'
import { getFileUrlByFileIdApi, htmlToPdfApi } from '@/api/up-file'
import {
  bidFileTypeDict,
  bidMarkTypeDict,
  reviewModelDict
} from '@/dictionary/project/kpb-project'
import { getQuoteOpenMarkPageApi } from '@/api/hall/quote-open'
import { format } from 'date-fns'
import DateUtils from '@/utils/date'
import { bidSubmitStatusDict } from '@/dictionary/project/bid'
import { fileDecodeStatusDict } from '@/dictionary/hall/open-bid-hall'
import { depositStatusDict } from '@/dictionary/project/common'

/**
 * 获取商务开标一览表打印数据
 * @param {Number} projectId 项目id
 * @returns {Promise<unknown>}
 */
const getBidOpenMark = projectId => {
  return new Promise((resolve, reject) => {
    getAllMarkListApi(projectId)
      .then(res => {
        resolve({ list: res.data || [] })
      })
      .catch(err => reject(err))
  })
}

/**
 * 获取报价开标一览表打印数据
 * @param {Number} projectId 项目id
 * @returns {Promise<unknown>}
 */
const getQuoteOpenMark = projectId => {
  return new Promise((resolve, reject) => {
    getQuoteOpenMarkPageApi(projectId, { isListing: 1 })
      .then(res => {
        resolve({ list: res.data?.records || [] })
      })
      .catch(err => reject(err))
  })
}

/**
 * 打印开标一览表pdf
 * @param {Array} list 表格数据
 * @param {Object} kpbProject 唱标列表
 * @param {Number} bidFileType 文件类型
 * @returns {Promise<unknown>}
 */
const getOpenMarkPdf = (list, kpbProject, bidFileType) => {
  const projectInfo = kpbProject.projectInfo || {}
  // 双信封第一阶段
  const isDouble = projectInfo.reviewModel === reviewModelDict.DOUBLE.value
  const isBidOpen = bidFileType === bidFileTypeDict.BUSINESS.value
  if (isDouble && isBidOpen) {
    return getDoubleBidOpenMarkPdf(list, kpbProject, bidFileType)
  }
  return getDefaultOpenMarkPdf(list, kpbProject, bidFileType)
}

const getDefaultOpenMarkPdf = (list, kpbProject, bidFileType) => {
  const projectInfo = kpbProject.projectInfo || {}
  const timeInfo = kpbProject.timeInfo || {}
  const isDouble = projectInfo.reviewModel === reviewModelDict.DOUBLE.value
  const markList = (kpbProject.markList || [])
    .filter(item => item.bidFileType === bidFileType)
    .map(item => {
      return {
        content: item.markUnit
          ? `${item.markName}(${item.markUnit})`
          : item.markName,
        id: item.markId
      }
    })
  const tableHeader = [
    { content: '序号' },
    ...markList,
    { content: '开标现场备注' },
    { content: isDouble ? '投标/响应代表签字' : '投标代表签字' }
  ]
  const tableData = list.map((bidder, index) => {
    const itemList = bidder.itemList || []
    return tableHeader.map(item => {
      if (item.id) {
        const bidderItem = itemList.find(i => i.markId === item.id) || {}
        return {
          content: bidderItem.markValue || '-',
          style: 'word-wrap: break-word;'
        }
      }
      if (item.content === '序号') {
        return {
          content: index + 1
        }
      }

      if (item.content === '开标现场备注') {
        const remark =
          bidFileType === bidFileTypeDict.QUOTATION.value
            ? bidder.openOfferBidRemark
            : bidder.openBidRemark
        return {
          content: remark || '-',
          style: 'word-wrap: break-word;'
        }
      }
      return {
        content: ''
      }
    })
  })
  // 设置固定的左浮动表头
  const fixedPrev = tableHeader.splice(0, 2)

  const isQuoteMark = bidFileType === bidFileTypeDict.QUOTATION.value
  let pdfTitle = projectInfo.projectName + '开标一览表'
  if (isQuoteMark) {
    pdfTitle = projectInfo.projectName + '报价开标一览表'
  }
  if (isDouble) {
    pdfTitle = '第一信封开标一览表'

    if (isQuoteMark) {
      pdfTitle = '第二信封开标一览表'
    }
  }

  let descriptionBottom = [
    [
      { label: '唱标', content: '', style: 'width: 50%; padding: 0 88px' },
      { label: '记录', content: '', style: 'width: 50%; padding: 0 88px' }
    ]
  ]
  if (isDouble) {
    descriptionBottom = [
      [
        {
          label: '招采/招商方代表',
          content: '',
          style: 'width: 50%; padding: 0 88px'
        },
        { label: '主持人', content: '', style: 'width: 50%; padding: 0 88px' }
      ],
      [
        { label: '监督人', content: '', style: 'width: 50%; padding: 0 88px' },
        {
          label: '开标日期',
          content: format(
            new Date(DateUtils.safeDate(timeInfo.openTime)),
            'yyyy-MM-dd'
          ),
          style: 'width: 50%; padding: 0 88px'
        }
      ]
    ]
  }
  const pdfTemplate = generateTableHtml({
    pdfTitle: pdfTitle,
    describeInfoTop: [
      [
        {
          label: '项目编号',
          content: projectInfo.projectCode,
          style: 'width: 50%; padding: 0 88px'
        },
        {
          label: '项目名称',
          content: projectInfo.projectName,
          style: 'width: 50%; padding: 0 88px'
        }
      ],
      [
        {
          label: '开标时间',
          content: timeInfo.openTime,
          style: 'width: 50%; padding: 0 88px'
        }
      ]
    ],
    describeInfoBottom: descriptionBottom,
    tableHead: {
      fixedPrev: fixedPrev,
      fixedLast: [],
      dynamic: tableHeader,
      dynamicNum: 6
    },
    tableData: tableData,
    tableConfig: {
      tableStyle: 'table-layout: fixed;',
      needTableSplit: true,
      contentMaxLength: 200
    }
  })

  const postData = {
    name: `${pdfTitle}.pdf`,
    content: pdfTemplate
  }

  return new Promise((resolve, reject) => {
    htmlToPdfApi(postData)
      .then(res => {
        getFileUrlByFileIdApi({ attachmentId: res.data })
          .then(res => {
            const { data = {} } = res
            window.open(data.external, '_self')
          })
          .catch(err => {
            console.log('获取文件地址失败：', err)
            reject(err)
          })
      })
      .catch(err => {
        console.log('htmlToPdfApi err', err)
        reject(err)
      })
  })
}

/**
 * 双信封第一阶段开标一览表
 * @param list
 * @param kpbProject
 * @param bidFileType
 * @returns {Promise<unknown>}
 */
const getDoubleBidOpenMarkPdf = (list, kpbProject, bidFileType) => {
  const projectInfo = kpbProject.projectInfo || {}
  const timeInfo = kpbProject.timeInfo || {}
  const isDouble = projectInfo.reviewModel === reviewModelDict.DOUBLE.value
  // 是否是投标文件
  const isBidFileType = bidFileType === bidFileTypeDict.BUSINESS.value
  const markList = (kpbProject.markList || [])
    .filter(item => item.bidFileType === bidFileType)
    .map(item => {
      return {
        markType: item.markType,
        content: item.markUnit
          ? `${item.markName}(${item.markUnit})`
          : item.markName,
        id: item.markId
      }
    })
  const supplierNameMarkIndex = markList.findIndex(
    item => item.markType === bidMarkTypeDict.BID_PERSON.value
  )
  markList.splice(supplierNameMarkIndex, 1)
  const extraMarkList = isBidFileType
    ? [
      { content: '投标单位' },
      { content: '保证金缴纳状态' },
      { content: '文件递交状态' },
      { content: '文件解密状态' }
    ]
    : []
  const tableHeader = [
    {
      content: '序号'
    },
    ...extraMarkList,
    ...markList,
    {
      content: '备注'
    },
    isDouble ? { content: '投标/响应代表签字' } : null
  ].filter(value => value)
  const tableData = list.map((bidder, index) => {
    const itemList = bidder.itemList || []
    return tableHeader.map(item => {
      if (item.id) {
        const bidderItem = itemList.find(i => i.markId === item.id) || {}
        return {
          content: bidderItem.markValue || '-',
          style: 'word-wrap: break-word;'
        }
      }
      if (item.content === '序号') {
        return {
          content: index + 1
        }
      }
      if (item.content === '投标保证金（元）') {
        return {
          content: ''
        }
      }
      if (item.content === '保证金缴纳状态') {
        return {
          content:
            depositStatusDict.translate(bidder.depositPayStatus)?.label || ''
        }
      }
      if (item.content === '投标单位') {
        return { content: bidder.bidderName }
      }
      if (item.content === '文件递交状态') {
        return {
          content:
            bidSubmitStatusDict.translate(bidder.bidSubmitStatus)?.label || '-'
        }
      }
      if (item.content === '文件解密状态') {
        return {
          content:
            fileDecodeStatusDict.translate(bidder.bidFileStatus)?.label || '-'
        }
      }
      if (item.content === '开标现场备注') {
        const remark =
          bidFileType === bidFileTypeDict.QUOTATION.value
            ? bidder.openOfferBidRemark
            : bidder.openBidRemark
        return {
          content: remark || '-',
          style: 'word-wrap: break-word;'
        }
      }
      return {
        content: ''
      }
    })
  })
  // 设置固定的左浮动表头
  const fixedPrev = tableHeader.splice(0, 2)

  const isQuoteMark = bidFileType === bidFileTypeDict.QUOTATION.value
  let pdfTitle = projectInfo.projectName + '开标一览表'
  if (isQuoteMark) {
    pdfTitle = projectInfo.projectName + '报价开标一览表'
  }
  if (isDouble) {
    pdfTitle = '第一信封开标一览表'

    if (isQuoteMark) {
      pdfTitle = '第二信封开标一览表'
    }
  }

  let descriptionBottom = [
    [
      { label: '唱标', content: '', style: 'width: 50%; padding: 0 88px' },
      { label: '记录', content: '', style: 'width: 50%; padding: 0 88px' }
    ]
  ]
  if (isDouble) {
    descriptionBottom = [
      [
        {
          label: '招采/招商方代表',
          content: '',
          style: 'width: 50%; padding: 0 88px'
        },
        { label: '主持人', content: '', style: 'width: 50%; padding: 0 88px' }
      ],
      [
        { label: '监督人', content: '', style: 'width: 50%; padding: 0 88px' },
        {
          label: '开标日期',
          content: format(
            new Date(DateUtils.safeDate(timeInfo.openTime)),
            'yyyy-MM-dd'
          ),
          style: 'width: 50%; padding: 0 88px'
        }
      ]
    ]
  }
  const pdfTemplate = generateTableHtml({
    pdfTitle: pdfTitle,
    describeInfoTop: [
      [
        {
          label: '项目编号',
          content: projectInfo.projectCode,
          style: 'width: 50%; padding: 0 88px'
        },
        {
          label: '项目名称',
          content: projectInfo.projectName,
          style: 'width: 50%; padding: 0 88px'
        }
      ],
      [
        {
          label: '开标时间',
          content: timeInfo.openTime,
          style: 'width: 50%; padding: 0 88px'
        }
      ]
    ],
    describeInfoBottom: descriptionBottom,
    tableHead: {
      fixedPrev: fixedPrev,
      fixedLast: [],
      dynamic: tableHeader,
      dynamicNum: 6
    },
    tableData: tableData,
    tableConfig: {
      tableStyle: 'table-layout: fixed;',
      needTableSplit: true,
      contentMaxLength: 160
    }
  })

  const postData = {
    name: `${pdfTitle}.pdf`,
    content: pdfTemplate
  }

  return new Promise((resolve, reject) => {
    htmlToPdfApi(postData)
      .then(res => {
        getFileUrlByFileIdApi({ attachmentId: res.data })
          .then(res => {
            const { data = {} } = res
            window.open(data.external, '_self')
          })
          .catch(err => {
            console.log('获取文件地址失败：', err)
            reject(err)
          })
      })
      .catch(err => {
        console.log('htmlToPdfApi err', err)
        reject(err)
      })
  })
}

/**
 * 开标一览表
 */
const useOpenMark = () => {
  return {
    getBidOpenMark,
    getQuoteOpenMark,
    getOpenMarkPdf
  }
}

export default useOpenMark
